import { Search, X, Download } from 'lucide-react'

/**
 * @param {object}   props
 * @param {object}   props.filters        — { from, to, carrier, state, search, tag }
 * @param {function} props.onFilterChange — (key, value) => void
 * @param {function} props.onClear
 * @param {function} [props.onExport]
 * @param {boolean}  [props.exporting]
 * @param {string[]} [props.carriers]     — dynamic carrier list from API
 * @param {string[]} [props.states]       — dynamic state list from API
 * @param {string[]} [props.tags]         — dynamic tag list from API
 * @param {string}   [props.minFrom]      — YYYY-MM-DD; earliest date the caller
 *                                          allows (client portal passes the
 *                                          open billing cycle's period_floor).
 *                                          Displayed as the Ship From value
 *                                          when the user hasn't set an
 *                                          explicit filter, and enforced as
 *                                          the input's `min` so the browser's
 *                                          date picker matches the notice.
 */
export default function FilterBar({ filters = {}, onFilterChange, onClear, onExport, exporting = false, carriers = [], states = [], tags = [], showCasePick = false, minFrom }) {
  const hasFilters = Object.values(filters).some((v) => v && v !== 'All')

  return (
    <div className="card p-4 mb-4">
      <div className="flex flex-wrap gap-3 items-end">
        {/* Ship date from — falls back to minFrom so the visible date matches
            the "showing orders from …" notice above the table. */}
        <div className="flex-shrink-0 w-full sm:w-auto">
          <label className="label">Ship From</label>
          <input
            type="date"
            value={filters.from || minFrom || ''}
            min={minFrom || undefined}
            onChange={(e) => onFilterChange('from', e.target.value)}
            className="input w-full sm:w-36"
          />
        </div>

        {/* Ship date to */}
        <div className="flex-shrink-0 w-full sm:w-auto">
          <label className="label">Ship To</label>
          <input
            type="date"
            value={filters.to || ''}
            min={minFrom || undefined}
            onChange={(e) => onFilterChange('to', e.target.value)}
            className="input w-full sm:w-36"
          />
        </div>

        {/* Carrier */}
        <div className="flex-shrink-0 w-full sm:w-auto">
          <label className="label">Carrier</label>
          <select
            value={filters.carrier || ''}
            onChange={(e) => onFilterChange('carrier', e.target.value)}
            className="input w-full sm:w-44"
          >
            <option value="">All Carriers</option>
            {carriers.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* State */}
        <div className="flex-shrink-0 w-full sm:w-auto">
          <label className="label">State</label>
          <select
            value={filters.state || ''}
            onChange={(e) => onFilterChange('state', e.target.value)}
            className="input w-full sm:w-36"
          >
            <option value="">All States</option>
            {states.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Case Pick */}
        {showCasePick && (
          <div className="flex-shrink-0 w-full sm:w-auto">
            <label className="label">Case Pick</label>
            <select
              value={filters.casePick || ''}
              onChange={(e) => onFilterChange('casePick', e.target.value)}
              className="input w-full sm:w-40"
            >
              <option value="">All Orders</option>
              <option value="yes">Case Pick Only</option>
              <option value="no">No Case Pick</option>
            </select>
          </div>
        )}

        {/* Tag */}
        {tags.length > 0 && (
          <div className="flex-shrink-0 w-full sm:w-auto">
            <label className="label">Tag</label>
            <select
              value={filters.tag || ''}
              onChange={(e) => onFilterChange('tag', e.target.value)}
              className="input w-full sm:w-40"
            >
              <option value="">All Tags</option>
              {tags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        {/* Search */}
        <div className="flex-1 min-w-[180px]">
          <label className="label">Search</label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Order # or recipient..."
              value={filters.search || ''}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="input pl-8"
            />
            {filters.search && (
              <button
                onClick={() => onFilterChange('search', '')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0 pb-0.5">
          {hasFilters && (
            <button onClick={onClear} className="btn-secondary">
              <X size={14} />
              Clear
            </button>
          )}
          {onExport && (
            <button onClick={onExport} disabled={exporting} className="btn-secondary">
              <Download size={14} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
